import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Autorenew as AutorenewIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from "@material-ui/icons";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";

import { debounce } from "lodash";
import ContainerHeader from "components/utils/ContainerHeader";
import Breadcrumbs from "components/utils/Breadcrumbs";
import DateTimePicker from "components/common/tools/DateTimePicker";
import { getOfferOptions } from "./formParams/freecopiesForm";
import OfferAPI from "../../api/OfferAPI";
import { getSelectClassNames } from "../form/Select";

import "css/admin/freecopy.scss";
import classNames from "classnames";

export const DURATIONS_TYPE = {
  DURATION_DATE: "DURATION_DATE",
  DURATION_DAYS: "DURATION_DAYS",
};

function FreecopyUniqueCodeForm({
  editMode,
  onSubmit,
  item,
  offerPlans,
  loading,
}) {
  const { t } = useTranslation("editor-freecopy");
  const { projectSlug } = useParams();
  const isLimitDateDuration = item?.usage_limit_date
    ? DURATIONS_TYPE.DURATION_DATE
    : DURATIONS_TYPE.DURATION_DAYS;

  const initialDurationType = editMode
    ? isLimitDateDuration
    : DURATIONS_TYPE.DURATION_DAYS;

  const [currentItem, setCurrentItem] = useState(null);

  const [durationType, setDurationType] = useState(null);

  const [validCode, setValidCode] = useState(null);

  useEffect(() => {
    setCurrentItem(item);
    setDurationType(initialDurationType);
  }, [item]);

  const checkFreecopyCode = useMutation({
    mutationFn: ({ code, projectSlug }) =>
      OfferAPI.checkFreecopyUniqueCode({ code, projectSlug }),
    onSuccess: ({ message }) => {
      setValidCode(message === "valid_code");
    },
    onError: () => {
      setValidCode(false);
    },
  });

  const checkCode = debounce((code) => {
    checkFreecopyCode.mutate({ code, projectSlug });
  }, 400);

  const onItemChange = ({ key, value }) => {
    setCurrentItem({
      ...currentItem,
      [key]: value,
    });
  };

  const onCodeChange = (event) => {
    const code = event.target.value;
    setCurrentItem({
      ...currentItem,
      code,
    });

    if (code) {
      checkCode(code);
    } else {
      setValidCode(null);
    }
  };

  const onChangeDurationType = (type) => {
    setDurationType(type);

    const itemChange =
      type === DURATIONS_TYPE.DURATION_DATE
        ? {
            days_duration: 0,
          }
        : {
            usage_limit_date: null,
          };

    setCurrentItem({
      ...currentItem,
      ...itemChange,
    });
  };

  const generateCode = () => {
    if (editMode) return;
    const code = Math.random().toString(36).substring(2, 9);
    setCurrentItem({
      ...currentItem,
      code,
    });
    checkCode(code);
  };

  const breadcrumbs = [
    {
      name: t("editor-menu:offerManagement"),
      url: "/freecopy",
    },
    {
      name: t("sectionTitleSimple"),
      url: "/freecopy",
    },
    {
      name: t(editMode ? "edit" : "new"),
    },
  ];

  const offerPlansOptions = getOfferOptions({ t, offerPlans });

  const currentOfferPlan = offerPlansOptions?.find(
    (option) => option.value === currentItem?.offer_plan_uuid,
  );

  const validCodeIcons = validCode ? (
    <CheckIcon className="icon-check valid" />
  ) : (
    <CloseIcon className="icon-check error" />
  );

  const renderIconsInput =
    validCode === null ? (
      <CheckIcon className="icon-check placeholder" />
    ) : (
      validCodeIcons
    );

  const status = currentItem?.enabled ? "active" : "stop";
  const classEdit = editMode ? "edit-mode" : "";

  const selectClassNames = getSelectClassNames();

  const codeClassNames = classNames(
    "editor-form-input",
    "input-check-code",
    "border-input!",
    { "bg-muted": editMode },
    classEdit,
  );

  const generateCodeButtonClassNames = classNames("generate-button", {
    "cursor-pointer": !editMode,
  });

  return (
    <div
      className="editor-container freecopy editor-has-breadcrumbs"
      data-testid="FreecopyUniqueCodeForm"
    >
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        item={{
          ...currentItem,
          label: editMode ? t("editLabelCode") : t("newLabelCode"),
        }}
        itemStatus={status}
        onChangeState={(status) => {
          onItemChange({ key: "enabled", value: status });
        }}
        onSave={() => {
          onSubmit({ ...currentItem, durationType, validCode });
        }}
        loading={loading}
      />
      {currentItem && (
        <div className="editor-tile-container">
          <div className="editor-form-section">
            <div className="editor-form-row">
              <div className="editor-form-label">{t("labelUniqueCode")}</div>
              <div className="editor-form-input">
                <input
                  placeholder={t("labelUniqueCodePlaceholder")}
                  type="text"
                  onChange={(e) =>
                    onItemChange({
                      key: "label",
                      value: e.target.value,
                    })
                  }
                  value={currentItem.label}
                  className="border-input!"
                />
              </div>
            </div>
          </div>
          <div className="editor-form-section">
            <div className="editor-form-row">
              <div className="editor-form-label">{t("labelCode")}</div>
              <div className={codeClassNames}>
                {renderIconsInput}
                <input
                  disabled={editMode}
                  placeholder={t("placeholderCode")}
                  type="text"
                  onChange={onCodeChange}
                  value={currentItem.code}
                />
                <button
                  className={generateCodeButtonClassNames}
                  disabled={editMode}
                  onClick={generateCode}
                  type="button"
                >
                  <AutorenewIcon className="icon" />
                </button>
              </div>
            </div>
          </div>
          <div className="editor-form-section">
            <div className="editor-form-row">
              <div className="editor-form-label">{t("offer")}</div>
              <div className="editor-form-input max-w-100">
                <Select
                  isDisabled={editMode}
                  classNames={selectClassNames}
                  classNamePrefix="freecopy-offer-select"
                  placeholder={t("selectOffer")}
                  onChange={(selection) =>
                    onItemChange({
                      key: "offer_plan_uuid",
                      value: selection.value,
                    })
                  }
                  value={currentOfferPlan}
                  options={offerPlansOptions}
                  isSearchable
                />
              </div>
            </div>
          </div>

          <div className="editor-form-section">
            <div className="editor-form-row">
              <div className="editor-form-label">{t("durationAccess")}</div>
              <div className="selectable-input-container">
                <div
                  className={`selectable-option ${durationType === DURATIONS_TYPE.DURATION_DAYS ? "selected" : ""}`}
                  onClick={() => {
                    onChangeDurationType(DURATIONS_TYPE.DURATION_DAYS);
                  }}
                >
                  <span className="selectable-option-label-input">
                    {t("day")}
                  </span>
                </div>
                <div
                  className={`selectable-option ${durationType === DURATIONS_TYPE.DURATION_DATE ? "selected" : ""}`}
                  onClick={() => {
                    onChangeDurationType(DURATIONS_TYPE.DURATION_DATE);
                  }}
                >
                  <span className="selectable-option-label-input">
                    {t("date")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {durationType === DURATIONS_TYPE.DURATION_DAYS && (
            <div className="editor-form-section">
              <div className="editor-form-row">
                <div className="editor-form-label">{t("duration")}</div>
                <div className="editor-form-input">
                  <div>
                    <input
                      type="number"
                      min={0}
                      value={currentItem.days_duration}
                      onChange={(e) =>
                        onItemChange({
                          key: "days_duration",
                          value: e.target.value,
                        })
                      }
                    />
                    <span className="duration-days">{t("days")}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {durationType === DURATIONS_TYPE.DURATION_DATE && (
            <div className="editor-form-section">
              <div className="editor-form-row">
                <div className="editor-form-label">{t("duration")}</div>
                <div className="editor-form-input">
                  <DateTimePicker
                    value={
                      currentItem.usage_limit_date
                        ? new Date(currentItem.usage_limit_date)
                        : undefined
                    }
                    onDateTimeChange={(date) =>
                      onItemChange({
                        key: "usage_limit_date",
                        value: moment(date).format(),
                      })
                    }
                    minDate={new Date()}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="editor-form-section">
            <div className="editor-form-row">
              <div className="editor-form-label">{t("activationsMax")}</div>
              <input
                type="number"
                min={0}
                value={currentItem.max_activations}
                onChange={(e) =>
                  onItemChange({
                    key: "max_activations",
                    value: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <div className="editor-form-section">
            <div className="editor-form-row">
              <div className="editor-form-label">{t("activationLimit")}</div>
              <DateTimePicker
                value={
                  currentItem.activation_limit_date
                    ? new Date(currentItem.activation_limit_date)
                    : undefined
                }
                onDateTimeChange={(date) =>
                  onItemChange({
                    key: "activation_limit_date",
                    value: moment(date).format(),
                  })
                }
                minDate={new Date()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

FreecopyUniqueCodeForm.defaultProps = {
  offerPlans: [],
  loading: false,
};

FreecopyUniqueCodeForm.propTypes = {
  item: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
  offerPlans: PropTypes.array,
  loading: PropTypes.bool,
};

export default FreecopyUniqueCodeForm;
